"use client";
import React, { useState, useCallback, Suspense } from "react";
import { usePathname } from "next/navigation";
import Sidebar from "./Sidebar";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { BillingIssueBanner } from "@/components/billing/BillingIssueBanner";
import { AppPromoBanner } from "@/components/shell/AppPromoBanner";
import { CancelRescueBanner } from "@/components/shell/CancelRescueBanner";
import { PushPromptBanner } from "@/components/shell/PushPromptBanner";
import { WHALE_WATCH_PROMO } from "@/components/shell/promo-config";
import { TutorialNudgeBanner } from "@/components/shell/TutorialNudgeBanner";
import { useWhatsNew, WhatsNew } from "./WhatsNew";
import { CommandPaletteMount } from "./CommandPaletteMount";
import { ShortcutHelp } from "./ShortcutHelp";
import { BottomTabBar } from "./BottomTabBar";
import { MobileOnboardingChecklist } from "@/components/onboarding/MobileOnboardingChecklist";
import { HiddenBetsManager } from "@/components/dismissed-bets/lazy-hidden-bets-manager";
import { useOptionalDismissedBets } from "@/contexts/dismissed-bets-context";
import type { WhaleWatchPersistedFilters } from "@/app/(app)/whale-watch/whale-watch-filters";
import { AppShellInitialStateProvider } from "./app-shell-initial-state-context";
import { ChatDock } from "@/components/chat/chat-shell";
import {
  TOOL_LOAD_CLIENT_BUILD_ATTESTATION,
  TOOL_LOAD_CLIENT_OBSERVABILITY_MODE,
} from "@/lib/performance/tool-load-timing";

const EMPTY_NUDGES: string[] = [];

interface AppShellProps {
  children: React.ReactNode;
  lastSeenUpdateId?: string | null;
  dismissedTutorialNudges?: string[];
  hasOnboardingPreferences?: boolean;
  checklistDismissed?: boolean;
  initialWhaleWatchFilters?: WhaleWatchPersistedFilters | null;
}

export function AppShell({ children, lastSeenUpdateId = null, dismissedTutorialNudges = EMPTY_NUDGES, hasOnboardingPreferences = false, checklistDismissed = false, initialWhaleWatchFilters }: AppShellProps) {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const closeMobileNav = useCallback(() => setIsMobileOpen(false), []);
  const pathname = usePathname();
  const dismissedBets = useOptionalDismissedBets();
  const hiddenBetCount = dismissedBets?.count ?? 0;
  const [lastSeenId, setLastSeenId] = useState(lastSeenUpdateId);
  const { hasUnread: hasUnreadUpdates } = useWhatsNew(lastSeenId);
  const isOptimizerRoute = pathname === "/optimizer" || pathname === "/optimizer/props";
  const isHomePage = pathname === "/home";
  const isAccountDashboard = pathname === "/account";
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(isOptimizerRoute);

  const isOptimizerPage = pathname === "/optimizer";
  const isPropsPage = pathname === "/optimizer/props";
  const isDiscrepanciesPage = pathname === "/prop-discrepancies";
  const isArbitragePage = pathname === "/arbitrage";
  const isLowHoldPage = pathname === "/low-hold";
  const isWhaleWatchPage = pathname === "/whale-watch" || pathname?.startsWith("/whale-watch/");
  const isOddsScreenPage = pathname?.startsWith("/odds-screen");
  const isEvSniperPage = pathname === "/ev-sniper" || pathname === "/edge-finder";
  const isDenseToolPage =
    isOptimizerPage ||
    isPropsPage ||
    isDiscrepanciesPage ||
    isArbitragePage ||
    isLowHoldPage ||
    isWhaleWatchPage ||
    isOddsScreenPage ||
    isEvSniperPage;
  const suppressWhatsNewAutoOpen = ["/fix-billing", "/account"].some((route) => pathname?.startsWith(route));

  return (
    <AppShellInitialStateProvider value={{ whaleWatchFilters: initialWhaleWatchFilters }}>
      <div
        data-authenticated-app-shell="true"
        data-tool-load-build-attestation={TOOL_LOAD_CLIENT_BUILD_ATTESTATION}
        data-tool-load-observability={TOOL_LOAD_CLIENT_OBSERVABILITY_MODE}
        className={cn(
          "w-full bg-optimizer-bg text-white flex flex-col",
          isDenseToolPage ? "h-dvh" : "min-h-dvh md:h-dvh"
        )}
      >
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-md focus:bg-background focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-foreground focus:shadow-lg"
        >
          Skip to content
        </a>

        <CommandPaletteMount />
        <ShortcutHelp />
        <WhatsNew
          lastSeenId={lastSeenId}
          onSeen={setLastSeenId}
          suppressAutoOpen={suppressWhatsNewAutoOpen}
        />

        {!isAccountDashboard && <BillingIssueBanner />}
        {!isAccountDashboard && <CancelRescueBanner />}
        {!pathname?.startsWith("/account") && <PushPromptBanner />}
        {!isDenseToolPage && !isAccountDashboard && <AppPromoBanner promo={WHALE_WATCH_PROMO} />}
        {!isDenseToolPage && !isAccountDashboard && (
          <TutorialNudgeBanner dismissedToolIds={dismissedTutorialNudges} />
        )}

        <div className="flex-1 flex min-h-0">
          <div className="hidden md:block">
            <Sidebar
              collapsed={isSidebarCollapsed}
              onToggle={() => setIsSidebarCollapsed((prev) => !prev)}
              hasOnboardingPreferences={hasOnboardingPreferences}
              checklistDismissed={checklistDismissed}
              showOnboardingChecklist={!isHomePage}
              hasUnreadUpdates={hasUnreadUpdates}
            />
          </div>

          <main
            id="main-content"
            tabIndex={-1}
            className={cn(
              "flex-1 min-w-0 min-h-0",
              isDenseToolPage
                ? "pb-[calc(3.5rem+env(safe-area-inset-bottom))] md:pb-0"
                : "p-4 pb-[calc(4rem+env(safe-area-inset-bottom))] md:overflow-y-auto md:p-4"
            )}
          >
            {children}
          </main>
          <ChatDock />
        </div>

        <BottomTabBar onMorePress={() => setIsMobileOpen(true)} isMoreOpen={isMobileOpen} />

        {!isHomePage && (
          <Suspense fallback={null}>
            <MobileOnboardingChecklist
              hasOnboardingPreferences={hasOnboardingPreferences}
              checklistDismissed={checklistDismissed}
            />
          </Suspense>
        )}

        <HiddenBetsManager />

        <Sheet open={isMobileOpen} onOpenChange={setIsMobileOpen}>
          <SheetContent
            id="mobile-nav-sheet"
            side="left"
            aria-label="Navigation"
            aria-describedby={undefined}
            className="odds-black md:hidden border-surface-line backdrop-blur-none shadow-[20px_0_60px_rgba(0,0,0,0.5)]"
          >
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <Sidebar
              variant="mobile"
              className="min-h-0 w-full flex-1"
              onNavigate={closeMobileNav}
              hasOnboardingPreferences={hasOnboardingPreferences}
              checklistDismissed={checklistDismissed}
              showOnboardingChecklist={false}
              hasUnreadUpdates={hasUnreadUpdates}
            />
          </SheetContent>
        </Sheet>
      </div>
    </AppShellInitialStateProvider>
  );
}
